import { StanCrop } from '@/components/core-components/StanCrop';
import { StanUnsplashImageSelector } from '@/components/core-components/StanUnsplashImageSelector/StanUnsplashImageSelector';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useModal } from '@/hooks/useModal';
import { stanNotify } from '@/lib/stanNotify';
import { Ref, useImperativeHandle, useRef, useState } from 'react';

interface StanUnsplashImageCropSelectorProps {
    onClose?: () => void;
    aspectRatio?: number;
    width?: number;
    height?: number;
    onCropped?: (croppedDataUrl: string, croppedFileName: string, croppedFileUrl: string) => void;
    onUploadError?: () => void;
}

export interface StanUnsplashImageCropSelectorRef {
    open: () => void;
}

export const StanUnsplashImageCropSelector = ({
    onClose,
    aspectRatio,
    width,
    height,
    onCropped,
    onUploadError,
    ref,
}: StanUnsplashImageCropSelectorProps & { ref?: Ref<StanUnsplashImageCropSelectorRef> }) => {
    const unsplashImageSelectorModal = useModal();
    const cropModal = useModal();
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const croppedDataUrlRef = useRef<string | null>(null);

    const onError = () => {
        setLoading(false);
        onUploadError?.();
        stanNotify({ type: 'error', title: 'Uh oh!', text: 'Failed to upload image, please try again.' });
        cropModal.close();
    };

    const onUploaded = (payload: { name: string; src: string }) => {
        setLoading(false);
        onCropped?.(croppedDataUrlRef.current ?? '', payload.name, payload.src);
        cropModal.close();
    };

    const { uploadSingleFile } = useFileUpload({
        objectType: 'image',
        onUploaded,
        onError,
    });

    const handleCropped = ({ croppedFile, croppedDataUrl }: { croppedFile: File; croppedDataUrl: string }) => {
        setLoading(true);
        croppedDataUrlRef.current = croppedDataUrl;
        uploadSingleFile(croppedFile, 0);
    };

    // Expose methods to parent component
    useImperativeHandle(
        ref,
        () =>
            ({
                open: () => {
                    unsplashImageSelectorModal.open();
                },
            }) as StanUnsplashImageCropSelectorRef
    );

    return (
        <div>
            <StanUnsplashImageSelector
                isOpen={unsplashImageSelectorModal.isOpen}
                onClose={() => {
                    onClose?.();
                    unsplashImageSelectorModal.close();
                }}
                onImagePicked={(file: File) => {
                    setFile(file);
                    unsplashImageSelectorModal.close();
                    cropModal.open();
                }}
            />

            <StanCrop
                isOpen={!!file && cropModal.isOpen}
                imageFile={file}
                aspectRatio={aspectRatio}
                width={width}
                height={height}
                isLoading={loading}
                onClose={() => {
                    onClose?.();
                    cropModal.close();
                }}
                onConfirm={handleCropped}
            />
        </div>
    );
};
